import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "pm_sq_banner_dismissed_until";

/**
 * Soft yellow nudge banner that appears on the dashboard when the signed-in
 * user hasn't set security questions yet. Dismissible for 7 days per click;
 * reappears automatically after that so the user doesn't lose the only
 * password-reset path we support.
 */
export default function SecurityQuestionsBanner({ userEmail }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < dismissedUntil) return;

    let cancelled = false;
    (async () => {
      try {
        // Admin shouldn't be nudged — they use the email reset path. Resolve
        // the admin email from backend config so there's no hardcoded value
        // drifting from the server-side ADMIN_EMAIL env.
        const cfg = await api.get("/auth/public-config");
        const adminEmail = (cfg.data?.admin_email || "").toLowerCase();
        if (adminEmail && (userEmail || "").toLowerCase() === adminEmail) return;
        const r = await api.get("/auth/security-questions/mine");
        if (!cancelled && !r.data?.has_questions) setShow(true);
      } catch {
        // Fail silent — the nudge is a UX nicety, not critical path.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  const dismiss = () => {
    // Snooze for 7 days.
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + 7 * 24 * 60 * 60 * 1000),
    );
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="pm-card p-4 flex items-start gap-3 grain"
      style={{
        background: "var(--hl-yellow)",
        borderLeft: "4px solid #c98a3b",
      }}
      data-testid="sq-nudge-banner"
    >
      <ShieldAlert
        size={20}
        className="mt-0.5 flex-none"
        style={{ color: "#c98a3b" }}
      />
      <div className="flex-1 text-sm">
        <div className="font-semibold mb-0.5">
          Set up security questions
        </div>
        <div style={{ color: "var(--text-secondary)" }}>
          Email-based password reset isn't available yet. Set two security
          questions so you can get back in if you forget your password.
        </div>
        <Link
          to="/app/security"
          className="inline-block mt-2 font-semibold text-sm underline"
          style={{ color: "var(--primary)" }}
          data-testid="sq-nudge-setup-link"
        >
          Set them up — 30 seconds
        </Link>
      </div>
      <button
        onClick={dismiss}
        className="flex-none p-1 rounded hover:opacity-70"
        aria-label="Dismiss"
        data-testid="sq-nudge-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
